import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Card, Alert } from 'react-bootstrap';
import Modal from '@rd-web-markets/shared/dist/util/Modal';
import { useTranslation } from 'react-i18next';
import { Button } from '@rd-web-markets/shared/dist/util/buttons';

const ChecklistModal = ({ show, onHide, checklistTemplate, onSubmit }) => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState({});
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(true);

  useEffect(() => {
    if (show && checklistTemplate) {
      const initialAnswers = {};
      checklistTemplate.checklist_rows.forEach(row => {
        initialAnswers[row.id] = {
          checklist_row_id: row.id,
          question: row.question,
          option_1: row.option_1,
          option_2: row.option_2,
          selected_option: '',
          comments: '',
          order_position: row.order_position
        };
      });
      setAnswers(initialAnswers);
      setSaveBtnDisabled(true);
    }
  }, [show, checklistTemplate]);

  useEffect(() => {
    if (!checklistTemplate) return;
    
    const allAnswered = checklistTemplate.checklist_rows.every(row => {
      const answer = answers[row.id];
      if (!answer || !answer.selected_option) return false;
      
      const requiresComments = answer.selected_option === 'option_1' 
        ? row.option_1_requires_comments 
        : row.option_2_requires_comments;
      
      if (requiresComments && !answer.comments?.trim()) return false;
      
      return true;
    });
    
    setSaveBtnDisabled(!allAnswered);
  }, [answers, checklistTemplate]);

  const handleOptionChange = (rowId, option) => {
    setAnswers(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        selected_option: option,
        comments: ''
      }
    }));
  };

  const handleCommentsChange = (rowId, comments) => {
    setAnswers(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        comments
      }
    }));
  };


  const handleSubmit = () => {
    onSubmit(Object.values(answers));
    setAnswers({});
  };

  if (!checklistTemplate) return null;

  return (
    <Modal
      size="xl"
      show={show}
      title={t('complete_checklist')}
      onHide={onHide}
      renderBody={() => (
        <Card className="p-3">
          <h5 className="mb-3">{t('answer_all_questions_to_proceed')}</h5>
          <p className="text-muted mb-4">
            {t('please_select_an_option_for_each_question_and_provide_comments_where_required')}
          </p>

          <div style={{ maxHeight: '50vh', overflowY: 'auto' }} className="mb-4">
            {checklistTemplate.checklist_rows.map((row, index) => {
              const answer = answers[row.id] || {};
              
              // Check if comments are required for selected option
              const requiresComments = answer.selected_option === 'option_1' 
                ? row.option_1_requires_comments 
                : answer.selected_option === 'option_2'
                ? row.option_2_requires_comments
                : false;
              
              return (
                <Card key={row.id} className="mb-3 p-3 border">
                  <h6 className="mb-3">
                    <strong>{index + 1}. {row.question}</strong>
                  </h6>
                  
                  <Form.Group className="mb-2">
                    <Card 
                      className={`p-3 mb-2 ${answer.selected_option === 'option_1' ? 'border-primary border-3 bg-primary bg-opacity-10' : 'border border-2'}`}
                    >
                      <Form.Check
                        type="radio"
                        id={`${row.id}-option1`}
                        name={`question-${row.id}`}
                        label={<strong>{row.option_1}</strong>}
                        checked={answer.selected_option === 'option_1'}
                        onChange={() => handleOptionChange(row.id, 'option_1')}
                        className="fs-6"
                      />
                    </Card>
                    
                    <Card 
                      className={`p-3 ${answer.selected_option === 'option_2' ? 'border-primary border-3 bg-primary bg-opacity-10' : 'border border-2'}`}
                    >
                      <Form.Check
                        type="radio"
                        id={`${row.id}-option2`}
                        name={`question-${row.id}`}
                        label={<strong>{row.option_2}</strong>}
                        checked={answer.selected_option === 'option_2'}
                        onChange={() => handleOptionChange(row.id, 'option_2')}
                        className="fs-6"
                      />
                    </Card>
                  </Form.Group>

                  {/* Only show comments field if option is selected AND comments are required */}
                  {answer.selected_option && requiresComments && (
                    <Form.Group className="mt-3">
                      <Form.Label>
                        {t('comments')}
                        <span className="text-danger"> *</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={answer.comments}
                        onChange={(e) => handleCommentsChange(row.id, e.target.value)}
                        placeholder={t('enter_your_comments')}
                      />
                    </Form.Group>
                  )}
                </Card>
              );
            })}
          </div>

          <div className="text-end">
            <Button
              variant="secondary"
              className="me-2"
              onClick={onHide}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={saveBtnDisabled}
              onClick={handleSubmit}
            >
              {t('submit_and_continue')}
            </Button>
          </div>
        </Card>
      )}
    />
  );
};

export default ChecklistModal;
